import colors from 'vuetify/util/colors'
import { assertIsNotUndefined } from '~/types/models'

const mn = '[cynqueModules/dataObjects/Avatar.ts]'

export function getAvatarColor(type: string, isDarkMode: boolean): string {
  const avatarcolors = [
    {
      type: 'teamMember',
      light: 'secondary-lighten-1',
      dark: 'secondary-darken-2',
    },

    {
      type: 'account',
      light: 'deep-orange-accent-2',
      dark: 'brown',
    },

    {
      type: 'companyContact',
      light: 'deep-orange-accent-2',
      dark: 'brown-darken-1',
    },
    {
      type: 'companyName',
      light: 'deep-orange-accent-2',
      dark: 'brown-darken-1',
    },

    {
      type: 'supplierContact',
      light: 'primary',
      dark: 'primary-darken-2',
    },
    {
      type: 'supplierName',
      light: 'primary',
      dark: 'primary-darken-2',
    },

    // ⚠ The dark value is a TUNED HEX, not a Material token, because Material's
    // deep-orange ramp bottoms out at darken-4 (#BF360C) and that was too bright
    // beside the rest of the dark set. Measured, on the #1E1E1E surface: every
    // other dark avatar sits at L* 24-41, and #BF360C led the whole group at
    // L* 43.9 while also carrying the highest chroma (74.5). The supplier purple
    // is just as saturated (C* 68.5) and does NOT glare — because it sits at
    // L* 24.1. So the fix is lightness, not chroma: this is #BF360C at 75%,
    // L* 32.8, which drops it just under the company brown (35.6) and the person
    // blue-grey (36.9) instead of leading them. Keeps the hue, so TSD still reads
    // as itself. Side benefit: the dark avatar's `text-grey-lighten-1` initials go
    // from 2.98:1 to 4.49:1 on the fill.
    //
    // Light is untouched — `yellow-darken-3` was never the complaint.
    { type: 'tsdContact', light: 'yellow-darken-3', dark: '#8f2909' },
    { type: 'tsdName', light: 'yellow-darken-3', dark: '#8f2909' },
    {
      type: 'leadSourceContact',
      light: 'grey-darken-1',
      dark: 'blue-grey-darken-2',
    },
    {
      type: 'person',
      light: 'grey-darken-1',
      dark: 'blue-grey-darken-2',
    },
    {
      type: 'user',
      light: 'grey-darken-1',
      dark: 'grey-darken-2',
    },
    {
      type: 'default',
      light: 'secondary-lighten-1',
      dark: 'secondary-darken-2',
    },
  ]
  let avatarColorObject = avatarcolors.find((clr) => clr.type === type)
  if (!avatarColorObject) {
    avatarColorObject = avatarcolors.find((clr) => clr.type === 'default')
  }
  assertIsNotUndefined(avatarColorObject)
  return isDarkMode ? avatarColorObject.dark : avatarColorObject.light
}

export function getAvatarHexColor(type: string, isDarkMode: boolean): string {
  const avatarHexColors = [
    {
      type: 'account',
      light: colors.deepOrange.accent2,
      dark: colors.brown.base,
    },
    {
      type: 'default',
      light: colors.blue.darken1,
      dark: colors.blue.darken1,
    },
  ]
  let avatarColorObject = avatarHexColors.find((clr) => clr.type === type)
  if (!avatarColorObject) {
    avatarColorObject = avatarHexColors.find((clr) => clr.type === 'default')
  }
  assertIsNotUndefined(avatarColorObject)
  return isDarkMode ? avatarColorObject.dark : avatarColorObject.light
}

export function getAvatarRounded(type: string): boolean | string {
  // The returned value feeds <v-avatar :rounded>. Entity/org avatars (company,
  // account, supplier, TSD) are rounded squares (`true` -> `.v-avatar--rounded`,
  // 4px); everyone else -- persons and contacts of every kind (companyContact/
  // supplierContact/tsdContact/leadSourceContact), plus team members and users --
  // is a circle. In V2 `rounded: false` produced that circle (v-avatar's default
  // shape). In V3 `:rounded="false"` instead emits the `rounded-0` class (a
  // SQUARE), so the circle must be requested explicitly with `'circle'` (which
  // emits `.rounded-circle`).
  const avatarRounded = [
    {
      type: 'companyName',
      rounded: true,
    },
    {
      type: 'account',
      rounded: true,
    },
    {
      type: 'supplierName',
      rounded: true,
    },
    {
      type: 'tsdName',
      rounded: true,
    },
    {
      type: 'default',
      rounded: 'circle',
    },
  ] as { type: string; rounded: boolean | string }[]
  let avatarRoundedObject = avatarRounded.find(
    (rounded) => rounded.type === type
  )
  if (!avatarRoundedObject) {
    avatarRoundedObject = avatarRounded.find(
      (rounded) => rounded.type === 'default'
    )
  }
  assertIsNotUndefined(avatarRoundedObject)
  return avatarRoundedObject.rounded
}

export function getAvatarTextColor(type: string, isDarkMode: boolean): string {
  const fn = `${mn} getAvatarTextColor |`
  if (type === 'account') {
    console.debug(`${fn} type is 'account'. darkMode is ${isDarkMode}.`)
    return isDarkMode ? 'text-brown' : 'text-deep-orange-accent-2'
  } else {
    console.debug(
      `${fn} type is NOT 'account'. It's '${type}'. darkMode is ${isDarkMode}.`
    )
    return isDarkMode ? 'text-grey-lighten-1' : 'text-white'
  }
}
